import { Trash, ShieldCheck, EnvelopeSimple, Info } from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

/**
 * PawLink Account & Data Deletion page (public URL).
 *
 * Required by Google Play "User Data" policy (2024+) and Apple App Store
 * Review Guideline 5.1.1(v). Both stores require a publicly reachable URL
 * that any user (installed or not) can visit to learn how to delete their
 * account and all associated data.
 *
 * Canonical URL: https://getpawlink.com/delete-account
 */
export default function DeleteAccount() {
    return (
        <LegalPageShell
            testId="delete-account-page"
            kicker="Your data, your control"
            title="Delete your PawLink account"
            sub="Deletion is permanent and free. Everything below runs the same on Web, PWA, Android, and iOS."
            updated="February 2026"
            icon={<Trash size={28} weight="fill" />}
        >
            <h2>What will be deleted</h2>
            <ul>
                <li>Your PawLink account and profile (name, email, hashed password, language preference)</li>
                <li>Every pet you have added and every QR code linked to your account</li>
                <li>All scan-event history (who scanned each QR, when, and from where)</li>
                <li>All notifications, saved locations, and community posts you have created</li>
                <li>All uploaded photos and audio you attached to your pets</li>
                <li>All active sessions on every device</li>
            </ul>

            <h2>What is retained (and why)</h2>
            <p>
                Some information may be kept for a short retention window when the law requires it
                (accounting records for tax authorities, fraud-prevention logs, or Paddle-issued
                refund records). This data is anonymised where possible and never linked back to
                your identity for marketing or product use. See the <a href="/privacy-policy">Privacy Policy</a>
                {" "}Section 8 for details.
            </p>

            <h2>How to delete your account</h2>

            <h3>Option 1 — In the app or website (fastest)</h3>
            <ol>
                <li>Sign in to PawLink on <a href="/login">the website</a> or the installed app.</li>
                <li>Open <a href="/account">Account</a> from the menu.</li>
                <li>Scroll to <strong>Danger zone</strong> and tap <strong>Delete my account</strong>.</li>
                <li>Confirm your email address in the dialog and tap <strong>Delete permanently</strong>.</li>
                <li>You will be signed out immediately and every piece of data listed above will be removed within seconds.</li>
            </ol>

            <h3>Option 2 — By email (if you cannot sign in)</h3>
            <p>
                Send an email from the address on your PawLink account to{" "}
                <a href={supportMailto({ subject: "PawLink — Delete my account" })}>{SUPPORT_EMAIL}</a>{" "}
                with the subject line <em>&ldquo;Delete my account&rdquo;</em>. Please include the
                pet name(s) you had registered so we can verify ownership. We reply to every
                request within <strong>1 business day</strong> and complete the deletion within{" "}
                <strong>7 calendar days</strong>. You will receive an email confirmation the moment
                the deletion completes.
            </p>

            <h2>Deletion timeline</h2>
            <ul>
                <li>In-app deletion: <strong>instant</strong> (all listed data is purged within seconds and cannot be recovered)</li>
                <li>Email request: within <strong>7 calendar days</strong> of the request being received</li>
                <li>Retained legal / accounting records: purged automatically after the mandatory retention period (typically 6–7 years depending on jurisdiction)</li>
            </ul>

            <h2>After deletion</h2>
            <ul>
                <li>Any printed copies of your pet&rsquo;s QR code that you displayed on a tag, collar, or sticker will show a &ldquo;Profile not found&rdquo; page when scanned.</li>
                <li>Your email address is released &mdash; you may register again with the same address whenever you like.</li>
                <li>Your data cannot be restored. If you change your mind, you will need to create a fresh account.</li>
            </ul>

            <h2>Third-party services</h2>
            <p>
                PawLink processes payments through Paddle (Merchant of Record) and sends
                transactional email through Resend. When we delete your PawLink account we also
                request deletion from these providers within the same window; retention there is
                governed by their own privacy policies:
            </p>
            <ul>
                <li><a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">Paddle Privacy Policy</a></li>
                <li><a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Resend Privacy Policy</a></li>
            </ul>

            <h2>Contact</h2>
            <p style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}>
                <EnvelopeSimple size={16} weight="regular" />
                <span>
                    Questions or need help? Email{" "}
                    <a href={supportMailto({ subject: "PawLink — Data deletion question" })}>{SUPPORT_EMAIL}</a>{" "}
                    or open a ticket at <a href="/support">Contact Support</a>. A real human on our
                    team will get back to you.
                </span>
            </p>

            <p style={{ display: "flex", gap: "0.5rem", alignItems: "baseline", marginTop: "1.5rem" }}>
                <ShieldCheck size={16} weight="regular" />
                <span>This page is the canonical &ldquo;Account &amp; Data Deletion&rdquo; URL referenced in the PawLink Google Play listing and the Apple App Store privacy report.</span>
            </p>

            <p style={{ display: "flex", gap: "0.5rem", alignItems: "baseline", marginTop: "0.5rem" }}>
                <Info size={16} weight="regular" />
                <span>Deleting your account also cancels any active PawLink Protection subscription. Refunds for the current billing period are handled per the <a href="/refund-policy">Refund Policy</a>.</span>
            </p>
        </LegalPageShell>
    );
}
